export function createDebounce<T extends(...args: any[]) => any>(
    fn: T,
    delay: number,
): {
    debounced: (...args: Parameters<T>) => void;
    cancel: () => void;
    flush: () => void;
    pending: () => boolean;
} {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let lastArgs: Parameters<T> | null = null;

    const debounced = (...args: Parameters<T>) => {
        lastArgs = args;

        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            if (lastArgs) {
                fn(...lastArgs);
                lastArgs = null;
            }
            timeoutId = null;
        }, delay);
    };

    const cancel = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
        lastArgs = null;
    };

    const flush = () => {
        if (timeoutId && lastArgs) {
            clearTimeout(timeoutId);
            fn(...lastArgs);
            timeoutId = null;
            lastArgs = null;
        }
    };

    const pending = () => Boolean(timeoutId);

    return {debounced, cancel, flush, pending};
}

/**
 * Vue 3 composable for debounced values
 */
import {ref, watch, onBeforeUnmount, Ref} from 'vue';

export function useDebouncedValue<T>(
    value: Ref<T>,
    delay: number,
): {
    debouncedValue: Ref<T>;
    pending: Ref<boolean>;
    flush: () => void;
} {
    const debouncedValue = ref<T>(value.value) as Ref<T>;
    const pending = ref(false);

    const {debounced, cancel, flush} = createDebounce((newValue: T) => {
        debouncedValue.value = newValue;
        pending.value = false;
    }, delay);

    watch(value, (newValue) => {
        pending.value = true;
        debounced(newValue);
    });

    onBeforeUnmount(() => {
        cancel();
    });

    return {
        debouncedValue,
        pending,
        flush: () => {
            flush();
            pending.value = false;
        },
    };
}

/**
 * Simple debounce function for general use
 * @param {T} fn - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @return {function} The debounced function
 */
export function debounce<T extends(...args: any[]) => any>(
    fn: T,
    delay: number,
): (...args: Parameters<T>) => void {
    const {debounced} = createDebounce(fn, delay);
    return debounced;
}

/**
 * Simple throttle function for general use
 * @param {T} fn - The function to throttle
 * @param {number} delay - The delay in milliseconds
 * @return {function} The throttled function
 */
export function throttle<T extends(...args: any[]) => any>(
    fn: T,
    delay: number,
): (...args: Parameters<T>) => void {
    let lastCallTime = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<T>) => {
        const now = Date.now();
        const timeSinceLastCall = now - lastCallTime;

        if (timeSinceLastCall >= delay) {
            lastCallTime = now;
            fn(...args);
        } else if (!timeoutId) {
            timeoutId = setTimeout(() => {
                lastCallTime = Date.now();
                fn(...args);
                timeoutId = null;
            }, delay - timeSinceLastCall);
        }
    };
}
